import { createElement, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../context/AuthProvider";
import { useModal } from "../../../context/ModalProvider";
import type { Sex, WeightUnit } from "../../modals/PhysiologyFields";
import {
  getAnonPreferencesDraft,
  setAnonPreferencesDraft
} from "../../../lib/anonDraft";
import {
  emptyCustomerProfile,
  loadProfile,
  overlayAnonPhysiologyOntoLoaded,
  physiologyFromInputs,
  saveProfile,
  setAnonPhysiologySession
} from "../../../lib/profileApi";
import {
  loadPreferences,
  savePreferences,
  type PreferenceOption,
  type PreferencesResponse,
  type SavePreferencesInput,
  type SerializedPreferences
} from "../../../lib/preferencesApi";
import { weightToKg } from "../../../lib/units";
import { clampCarbTarget, clampSodiumTarget } from "../../../lib/fuelClamps";
import { estimateRaceDayCarbsPerHour } from "@shared/modules/fuelPlans/fuelEstimate";
import {
  getFuelingProfile,
  patchFuelingProfile,
  type FuelingProfilePatch,
  type FuelingProfileView
} from "../../../lib/fuelingProfileApi";
import { DisableRampSheet } from "./DisableRampSheet";

/**
 * `useAboutYouFuel` — the single home for the athlete "About you" + "Your fuel
 * profile" state (profile-steps single-source, plan A). Extracted verbatim from
 * `TrainingPlanWizard` so THREE surfaces share one implementation of hydration,
 * dirty-checked saves and the fueling-profile patch:
 *
 *   - the race wizard (`<FuelExperienceStep>` / `<BodyStep>` / `<NeedsTastesStep>`),
 *   - the SAME wizard's no-race path (finishes with a rolling journey),
 *   - the My Fuel `ProfileGateSheet` (the same steps, gate variant).
 *
 * It owns ONLY athlete-state — physiology (Customer row), dietary/flavor
 * (CustomerPreferences), and the fueling profile (sweat→sodium, gut level,
 * per-sport targets, the gut ramp). EVENT-scoped fields (drop cadence, the
 * race-day carb anchor, the journey window) stay with each caller; the wizard
 * keeps them and reads `raceDayCarbs`/`targetsInPlay` here only to build its
 * journey body. Anon principals persist to browser drafts (reconciled on login),
 * exactly as before.
 */

type EstimatePolicy = Parameters<typeof estimateRaceDayCarbsPerHour>[0];

export interface UseAboutYouFuelOptions {
  /** Policy block that drives the live race-day carb estimate (from the plan
   *  library). Absent (no-event gate/setup) → the estimate is null and the
   *  race-day field, which the gate variant hides anyway, keeps its default. */
  fuelEstimatePolicy?: EstimatePolicy;
  /** Goal finish in minutes, for the race-day estimate. Absent → a 4h default;
   *  irrelevant to the gate variant (no race-day field shown). */
  goalMinutes?: number | null;
}

/**
 * The wizard edits `dietaryNeeds` (allergens) + flavor prefer/avoid; the save
 * endpoint (and anon draft) take the full preferences body, so merge those onto
 * whatever is already saved — never clobber product-types/brands/caffeine set in
 * the Preferences modal. `dietaryPreferences` (eating style) is no longer edited
 * anywhere in the UI; carry any previously-saved value through untouched.
 */
function withAboutYouPrefs(
  cur: SerializedPreferences | SavePreferencesInput | null,
  fields: {
    dietaryNeeds: string[];
    preferredFlavorProfiles: string[];
    excludedFlavorProfiles: string[];
  }
): SavePreferencesInput {
  return {
    dietaryNeeds: fields.dietaryNeeds,
    dietaryPreferences: cur?.dietaryPreferences ?? [],
    preferredProductTypes: cur?.preferredProductTypes ?? [],
    excludedProductTypes: cur?.excludedProductTypes ?? [],
    preferredFlavorProfiles: fields.preferredFlavorProfiles,
    excludedFlavorProfiles: fields.excludedFlavorProfiles,
    preferredBrands: cur?.preferredBrands ?? [],
    excludedBrands: cur?.excludedBrands ?? [],
    caffeineAllowed: cur?.caffeineAllowed ?? true,
    caffeineMaxMgPerHour: cur?.caffeineMaxMgPerHour ?? null,
    caffeineMaxPercentOfFuel: cur?.caffeineMaxPercentOfFuel ?? 50
  };
}

export function useAboutYouFuel(options: UseAboutYouFuelOptions = {}) {
  const { state: authState, refreshCsrf } = useAuth();
  const { open: openModal, close: closeModal } = useModal();
  const isCustomer = authState.kind === "customer";
  const csrfToken =
    authState.kind === "customer" || authState.kind === "anon" ? authState.csrfToken : null;

  // v4 fuel profile + drop cadence (Preview step). Gut answer suggests the ramp
  // default; the two stay independently editable.
  const [gutLevel, setGutLevel] = useState<"new" | "tuning" | "locked" | null>(null);
  const [rampEnabled, setRampEnabled] = useState(false);
  // Sweat starts UNSELECTED (D-11): nothing renders picked until the athlete
  // chooses (the "Not sure" option remains pickable) or a saved proxy sodium
  // hydrates below. Both null and "unsure" persist as an UNSET sodium (null) —
  // downstream that reads as the engine's average-sodium default
  // (`sweatLevelFromSodium(null)` → null → average), so there is no new server
  // enum value; the difference is purely that nothing renders selected and the
  // recap can say "sweat not set".
  const [sweat, setSweat] = useState<"unsure" | "lightly" | "somewhat" | "pretzel" | null>(null);
  // Athlete fueling profile — prefilled so returning users see their saved
  // answers instead of being re-asked (fueling-profile plan P2.3). When the
  // ramp is ALREADY running it renders read-only: a wizard toggle-off would
  // silently affect every race plan (R9).
  const [profileView, setProfileView] = useState<FuelingProfileView | null>(null);
  const sweatTouchedRef = useRef(false);
  const gutTouchedRef = useRef(false);
  const perSportTouchedRef = useRef(false);
  const rampTouchedRef = useRef(false);
  const rampAlreadyRunning = profileView?.profile?.rampEnabled === true;
  // T3b — custom carb targets. Race-day anchor defaults for the locked-in path;
  // per-sport anchors optional (0 = unset). The panel opens for locked-in, or
  // via the "enter your own targets" link for anyone (§3.6 coach case).
  const [raceDayCarbs, setRaceDayCarbs] = useState(75);
  const [runCarbs, setRunCarbs] = useState(0);
  const [rideCarbs, setRideCarbs] = useState(0);
  // W-17 — explicit sodium mg/hr for locked-in athletes (targets card's 4th
  // field). 0 = unset (keep the sweat-derived estimate); > 0 overrides it and
  // persists as an EXPLICIT sodium target.
  const [sodiumMg, setSodiumMg] = useState(0);
  const sodiumTouchedRef = useRef(false);
  const [showTargets, setShowTargets] = useState(false);
  // Once the user types a race-day number it's theirs; until then the field
  // tracks the live policy estimate (never the old hardcoded 75).
  const raceTargetTouchedRef = useRef(false);

  // "About you" — physiology (Customer row) + allergens (CustomerPreferences).
  // Prefilled from the saved profile/preferences (anon: local drafts) and saved
  // before the journey create, so the first drop's athlete snapshot reflects it.
  const [sex, setSex] = useState<Sex | null>(null);
  const [weightText, setWeightText] = useState("");
  const [weightUnit, setWeightUnit] = useState<WeightUnit>("lb");
  const [dietaryNeeds, setDietaryNeeds] = useState<Set<string>>(new Set());
  const [dietaryOptions, setDietaryOptions] = useState<PreferenceOption[]>([]);
  // Flavor preferences — the same paired prefer/avoid lists the Preferences
  // modal edits (`preferred/excludedFlavorProfiles`), surfaced here as two
  // always-visible chip rows (the round-3 collapse pattern is gone, D-16). A
  // flavor is mutually exclusive across the two rows (preferring drops any avoid).
  const [flavorPrefs, setFlavorPrefs] = useState<Set<string>>(new Set());
  const [flavorAvoids, setFlavorAvoids] = useState<Set<string>>(new Set());
  const [flavorOptions, setFlavorOptions] = useState<PreferenceOption[]>([]);
  const flavorTouchedRef = useRef(false);
  // Loaded preferences row (customer) — dietary saves merge into it so the
  // full-body POST never clobbers flavours/brands/caffeine set elsewhere.
  const savedPrefsRef = useRef<SerializedPreferences | null>(null);
  // Baseline for the dirty checks; null until the prefill lands. Touched flags
  // double as (a) hydration guards — a slow prefill must not clobber typed
  // values — and (b) the dirty fallback when the loads failed entirely.
  const profileBaselineRef = useRef<{
    sex: Sex | null;
    weight: number | null;
    weightUnit: WeightUnit;
    dietary: string;
    flavorPrefer: string;
    flavorAvoid: string;
  } | null>(null);
  const physiologyTouchedRef = useRef(false);
  const dietaryTouchedRef = useRef(false);
  // True once the initial profile/preferences/fueling load has resolved — the My
  // Fuel gate waits on this so it never fires against a not-yet-hydrated profile.
  const [hydrated, setHydrated] = useState(false);
  // The preferences request FAILED (vs. succeeded with empty lists): the
  // allergen/flavor option lists are unknown, so the step must say so and offer
  // a retry rather than silently hide a safety input.
  const [optionsLoadFailed, setOptionsLoadFailed] = useState(false);
  const retryCtrlRef = useRef<AbortController | null>(null);

  /**
   * Apply a preferences response (or the anon draft when null): option lists,
   * the merge base for saves, and the saved selections (touched refs win so a
   * late load never clobbers chips). Returns the baseline slice for the dirty
   * checks. Shared by the mount hydrate and `retryOptions`.
   */
  function applyPreferences(prefRes: PreferencesResponse | null) {
    if (prefRes) {
      setDietaryOptions(prefRes.options.dietaryNeeds);
      setFlavorOptions(prefRes.options.flavorProfiles);
    }
    savedPrefsRef.current = prefRes?.preferences ?? null;
    const prefs = prefRes?.preferences ?? getAnonPreferencesDraft<SavePreferencesInput>();
    const dietary = [...(prefs?.dietaryNeeds ?? [])];
    const flavorLoved = [...(prefs?.preferredFlavorProfiles ?? [])];
    const flavorAvoided = [...(prefs?.excludedFlavorProfiles ?? [])];
    if (!dietaryTouchedRef.current) setDietaryNeeds(new Set(dietary));
    if (!flavorTouchedRef.current) {
      setFlavorPrefs(new Set(flavorLoved));
      setFlavorAvoids(new Set(flavorAvoided));
    }
    return {
      dietary: [...dietary].sort().join("\n"),
      flavorPrefer: [...flavorLoved].sort().join("\n"),
      flavorAvoid: [...flavorAvoided].sort().join("\n")
    };
  }

  /** Re-run ONLY the preferences load after a failure (profile/fueling are
   *  untouched). A newer click supersedes an in-flight retry. */
  function retryOptions() {
    retryCtrlRef.current?.abort();
    const ctrl = new AbortController();
    retryCtrlRef.current = ctrl;
    loadPreferences(ctrl.signal).then(
      (prefRes) => {
        if (ctrl.signal.aborted) return;
        const prefsBaseline = applyPreferences(prefRes);
        if (profileBaselineRef.current) {
          profileBaselineRef.current = { ...profileBaselineRef.current, ...prefsBaseline };
        }
        setOptionsLoadFailed(false);
      },
      () => {
        if (!ctrl.signal.aborted) setOptionsLoadFailed(true);
      }
    );
  }

  useEffect(() => {
    const ctrl = new AbortController();
    let prefFailed = false;
    // The loads degrade independently — a failure just leaves blank fields
    // (nothing saved unless the user then types something); a preferences
    // failure is also flagged so the step can offer a retry.
    Promise.all([
      loadPreferences(ctrl.signal).catch(() => {
        prefFailed = true;
        return null;
      }),
      loadProfile(ctrl.signal).catch(() => null),
      getFuelingProfile(ctrl.signal).catch(() => null)
    ]).then(([prefRes, profileRes, fuelingRes]) => {
      if (ctrl.signal.aborted) return;
      setOptionsLoadFailed(prefFailed);
      // Fueling prefill — saved athlete answers pre-select; touched flags keep
      // a slow load from clobbering anything the user already changed.
      if (fuelingRes) {
        setProfileView(fuelingRes);
        const fp = fuelingRes.profile;
        if (fp) {
          if (fp.gutLevel && !gutTouchedRef.current) {
            setGutLevel(fp.gutLevel);
            if (fp.gutLevel === "locked") setShowTargets(true);
            else if (fp.gutLevel === "tuning") {
              const savedRun = fp.perSportTargets?.run ?? 0;
              const savedRide = fp.perSportTargets?.ride ?? 0;
              if (savedRun > 0 || savedRide > 0) setShowTargets(true);
            } else {
              setShowTargets(false);
            }
          }
          if (!rampTouchedRef.current) setRampEnabled(fp.rampEnabled || fp.gutLevel === "new");
          if (fp.sodiumSource === "proxy" && fp.sodiumTarget && !sweatTouchedRef.current) {
            setSweat(fp.sodiumTarget <= 400 ? "lightly" : fp.sodiumTarget >= 650 ? "pretzel" : "somewhat");
          }
          // A saved EXPLICIT sodium prefills the targets card's sodium field so
          // returning athletes see their number (typing overrides; W-17).
          if (fp.sodiumSource === "explicit" && fp.sodiumTarget && !sodiumTouchedRef.current) {
            setSodiumMg(fp.sodiumTarget);
          }
          if (!perSportTouchedRef.current) {
            const run = fp.perSportTargets?.run ?? 0;
            const ride = fp.perSportTargets?.ride ?? 0;
            if (run > 0) setRunCarbs(run);
            if (ride > 0) setRideCarbs(ride);
          }
        }
      }
      const prefsBaseline = applyPreferences(prefRes);
      const phy = isCustomer
        ? (profileRes ?? emptyCustomerProfile())
        : overlayAnonPhysiologyOntoLoaded(profileRes ?? emptyCustomerProfile());
      if (!physiologyTouchedRef.current) {
        setSex(phy.sex);
        setWeightText(phy.weight != null ? String(phy.weight) : "");
        setWeightUnit(phy.weightUnit ?? "lb");
      }
      profileBaselineRef.current = {
        sex: phy.sex,
        weight: phy.weight,
        weightUnit: phy.weightUnit ?? "lb",
        ...prefsBaseline
      };
      setHydrated(true);
    });
    return () => {
      ctrl.abort();
      retryCtrlRef.current?.abort();
    };
  }, [isCustomer]);

  // ── Derived ────────────────────────────────────────────────────────────────
  // Unset/"unsure" DISPLAYS the average (525) so the race-day card / recap
  // estimates aren't blank, but it is NOT persisted (see
  // `buildFuelingProfilePatch` — both clear sodium to null, the engine's
  // average-sodium default).
  const sodiumTarget = sweat === "lightly" ? 350 : sweat === "pretzel" ? 700 : 525;
  /** True when the athlete hasn't picked a real sweat level (unset, or the
   *  pickable "Not sure") — we persist NO sodium (null) so downstream uses the
   *  average-sodium default. */
  const sweatUnsure = sweat === "unsure" || sweat === null;
  const targetsInPlay = gutLevel === "locked" || (gutLevel === "tuning" && showTargets);
  /** The athlete emptied the sodium field with the targets panel open — a
   *  deliberate "no exact target any more". It is the only way back out of an
   *  exact mg/hr, so it has to release `hasExplicitSodium`: otherwise the save
   *  omits sodium entirely and the saved number silently stands. */
  const sodiumCleared = targetsInPlay && sodiumTouchedRef.current && sodiumMg <= 0;
  // A returning user with an EXACT sodium target keeps it unless they actively
  // pick a sweat level or clear the field — the wizard's proxies must not
  // clobber precision, but the athlete's own clear must land.
  const hasExplicitSodium =
    profileView?.profile?.sodiumSource === "explicit" &&
    (profileView.profile.sodiumTarget ?? 0) > 0 &&
    !sweatTouchedRef.current &&
    !sodiumCleared;

  const visibleDietaryCount = dietaryNeeds.size;
  const hasDietarySection = dietaryOptions.length > 0;

  // Policy-derived race-day estimate: duration tier from the goal finish time +
  // weight multiplier + run sport cap + sex-bias band position (same math as the
  // engine). Null when the policy block is missing (deploy skew / no-event gate).
  const estimatedRaceCarbs = useMemo(() => {
    const weight = physiologyFromInputs({ sex, weightText, weightUnit }).weight;
    return estimateRaceDayCarbsPerHour(options.fuelEstimatePolicy, {
      goalMinutes: options.goalMinutes ?? 240,
      sex,
      weightKg: weight != null ? weightToKg(weight, weightUnit) : null
    });
  }, [options.fuelEstimatePolicy, options.goalMinutes, sex, weightText, weightUnit]);
  // A returning athlete's saved race-day/run target (B6). Prefer an explicit
  // saved race-day per-sport target, else the run number (races run); 0 = unset.
  const savedRaceDayTarget = useMemo(() => {
    const ps = profileView?.profile?.perSportTargets ?? null;
    const raceDay = ps?.raceDay ?? 0;
    const run = ps?.run ?? 0;
    return raceDay > 0 ? raceDay : run > 0 ? run : 0;
  }, [profileView]);
  // Keep the custom-targets race-day field on the best default until the user
  // types their own number: the athlete's saved race-day/run target when they
  // have one (locked-in athletes fuel to it), else the live policy estimate.
  useEffect(() => {
    if (raceTargetTouchedRef.current) return;
    if (savedRaceDayTarget > 0) {
      setRaceDayCarbs(savedRaceDayTarget);
      return;
    }
    if (estimatedRaceCarbs == null) return;
    setRaceDayCarbs(estimatedRaceCarbs);
  }, [estimatedRaceCarbs, savedRaceDayTarget]);

  const targetsExpanded = targetsInPlay;
  const showCarbTargetsCard = gutLevel === "tuning" || gutLevel === "locked";
  // W-17 — the sodium the review card shows for "Sodium during": an explicit
  // mg/hr (typed this session or hydrated from a saved explicit target) wins
  // over the sweat-derived estimate whenever the targets panel is in play —
  // product decision 2026-08-18: a set sodium number always overrides the
  // sweat answer. 0 keeps the sweat-derived estimate.
  const effectiveSodium =
    targetsInPlay && sodiumMg > 0 ? clampSodiumTarget(sodiumMg) : sodiumTarget;

  const isComplete = gutLevel != null;

  // ── Save primitives ──────────────────────────────────────────────────────
  /**
   * Persist the "About you" fields (physiology → Customer row, dietary + flavor
   * → CustomerPreferences). Dirty-checked so an untouched step costs no writes
   * (the preferences POST syncs to Shopify metafields). Anon → browser drafts.
   */
  async function saveAboutYou() {
    const physiology = physiologyFromInputs({ sex, weightText, weightUnit });
    const base = profileBaselineRef.current;
    const physiologyDirty = base
      ? base.sex !== physiology.sex ||
        base.weight !== physiology.weight ||
        (physiology.weight != null && base.weightUnit !== physiology.weightUnit)
      : physiologyTouchedRef.current;
    const dietarySorted = [...dietaryNeeds].sort();
    const flavorPreferSorted = [...flavorPrefs].sort();
    const flavorAvoidSorted = [...flavorAvoids].sort();
    const dietaryDirty = base
      ? base.dietary !== dietarySorted.join("\n")
      : dietaryTouchedRef.current;
    const flavorDirty = base
      ? base.flavorPrefer !== flavorPreferSorted.join("\n") ||
        base.flavorAvoid !== flavorAvoidSorted.join("\n")
      : flavorTouchedRef.current;
    // Dietary + flavor both live on the preferences body — one POST covers both.
    const prefsDirty = dietaryDirty || flavorDirty;
    if (!physiologyDirty && !prefsDirty) return;

    const prefsFields = {
      dietaryNeeds: dietarySorted,
      preferredFlavorProfiles: flavorPreferSorted,
      excludedFlavorProfiles: flavorAvoidSorted
    };
    if (!isCustomer) {
      // Anon: browser drafts (28-day TTL) — attached to the account on login.
      if (physiologyDirty) setAnonPhysiologySession(physiology);
      if (prefsDirty) {
        setAnonPreferencesDraft<SavePreferencesInput>(
          withAboutYouPrefs(getAnonPreferencesDraft<SavePreferencesInput>(), prefsFields)
        );
      }
      return;
    }
    if (!csrfToken) return;
    if (physiologyDirty) await saveProfile(physiology, csrfToken, refreshCsrf);
    if (prefsDirty) {
      await savePreferences(
        withAboutYouPrefs(savedPrefsRef.current, prefsFields),
        csrfToken,
        refreshCsrf
      );
    }
  }

  /**
   * ATHLETE-state → fueling-profile patch (sodium / gut level / per-sport carb
   * targets / ramp). Untouched saved values re-send identically (harmless
   * upsert). Event-scoped fueling (the race-day anchor) is NOT here — that rides
   * each caller's journey body (D2).
   */
  function buildFuelingProfilePatch(): FuelingProfilePatch {
    // 0 = unset. Typed values are clamped to the UI band (shared clampCarbTarget)
    // — the number-input min/max only guide the spinner; an out-of-band value
    // would 400 opaquely (C5).
    const clampCarb = clampCarbTarget;
    const targetsActive = gutLevel === "locked" || (gutLevel === "tuning" && showTargets);
    const fuelingProfile: FuelingProfilePatch = {};
    if (gutLevel) fuelingProfile.gutLevel = gutLevel;
    // W-17 — an explicit sodium mg/hr (targets panel in play) wins over the
    // sweat proxy, whether typed this session or hydrated from a saved
    // explicit target — product decision 2026-08-18: a set sodium number
    // always overrides the sweat answer. Clamped to the shared engine
    // band (C5). Clearing the field to 0 releases `hasExplicitSodium`
    // (`sodiumCleared`) and hands control back to the sweat branches below,
    // which send an explicit null when no sweat level is picked.
    const explicitSodiumMg =
      targetsActive && sodiumMg > 0 ? clampSodiumTarget(sodiumMg) : 0;
    if (explicitSodiumMg > 0) {
      fuelingProfile.sodiumTarget = explicitSodiumMg;
      fuelingProfile.sodiumSource = "explicit";
    } else if (!hasExplicitSodium) {
      if (sweatUnsure) {
        // "Not sure" → clear any prior proxy so downstream falls to the
        // average-sodium default. Never overwrites an EXPLICIT target (guarded
        // by `hasExplicitSodium` above — a returning user's exact mg/hr stands).
        fuelingProfile.sodiumTarget = null;
        fuelingProfile.sodiumSource = null;
      } else {
        fuelingProfile.sodiumTarget = sodiumTarget;
        fuelingProfile.sodiumSource = "proxy";
      }
    }
    if (targetsActive) {
      const merged: Record<string, number> = {
        ...(profileView?.profile?.perSportTargets ?? {})
      };
      if (clampCarb(runCarbs) > 0) merged.run = clampCarb(runCarbs);
      else delete merged.run;
      if (clampCarb(rideCarbs) > 0) merged.ride = clampCarb(rideCarbs);
      else delete merged.ride;
      fuelingProfile.perSportTargets = Object.keys(merged).length > 0 ? merged : null;
    }
    // A running ramp is read-only here (R9); first enable lets the server
    // default the config (startedYmd = today, plan Q1).
    if (!rampAlreadyRunning) fuelingProfile.rampEnabled = rampEnabled;
    return fuelingProfile;
  }

  // Combined save — the no-event sheet + the My Fuel gate persist BOTH halves
  // here (the wizard drives the two primitives itself, profile-first). Manages
  // its own busy/error so those callers can render a single footer button.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      await saveAboutYou();
      // Anon carries a csrfToken too — the PATCH writes the anon-scoped profile
      // row (reconciled on login), exactly as the wizard apply path does.
      if (csrfToken) await patchFuelingProfile(buildFuelingProfilePatch(), csrfToken, refreshCsrf);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Couldn't save your preferences. Try again.");
      setSaving(false);
      throw err;
    }
    setSaving(false);
  }

  /**
   * Disable the athlete's running gut ramp (a FuelingProfile-level change — the
   * PATCH fans out a regen across every plan, R2). Confirmed first because it
   * touches all of their workouts. On success we mirror the new state locally so
   * the "already running" card flips back to the normal (off) ramp toggle.
   */
  function onDisableRamp() {
    const id = openModal(
      createElement(DisableRampSheet, {
        onConfirm: async () => {
          if (!csrfToken)
            throw new Error("Couldn't update your fueling profile — refresh and try again.");
          const view = await patchFuelingProfile({ rampEnabled: false }, csrfToken, refreshCsrf);
          setProfileView(view);
          setRampEnabled(false);
          rampTouchedRef.current = true;
          closeModal(id);
        },
        onClose: () => closeModal(id)
      })
    );
  }

  return {
    // hydration / completeness
    hydrated,
    isComplete,
    saving,
    saveError,
    // physiology
    sex,
    setSex,
    weightText,
    setWeightText,
    weightUnit,
    setWeightUnit,
    physiologyTouchedRef,
    // sweat → sodium
    sweat,
    setSweat,
    sweatTouchedRef,
    sweatUnsure,
    sodiumTarget,
    hasExplicitSodium,
    // dietary
    dietaryNeeds,
    setDietaryNeeds,
    dietaryOptions,
    dietaryTouchedRef,
    visibleDietaryCount,
    hasDietarySection,
    // option lists (allergens + flavors): load failure + retry
    optionsLoadFailed,
    retryOptions,
    // flavor
    flavorPrefs,
    setFlavorPrefs,
    flavorAvoids,
    setFlavorAvoids,
    flavorOptions,
    flavorTouchedRef,
    // gut / ramp
    gutLevel,
    setGutLevel,
    gutTouchedRef,
    rampEnabled,
    setRampEnabled,
    rampTouchedRef,
    rampAlreadyRunning,
    profileView,
    onDisableRamp,
    // per-sport + race-day targets
    raceDayCarbs,
    setRaceDayCarbs,
    raceTargetTouchedRef,
    runCarbs,
    setRunCarbs,
    rideCarbs,
    setRideCarbs,
    perSportTouchedRef,
    sodiumMg,
    setSodiumMg,
    sodiumTouchedRef,
    effectiveSodium,
    showTargets,
    setShowTargets,
    showCarbTargetsCard,
    targetsExpanded,
    targetsInPlay,
    estimatedRaceCarbs,
    savedRaceDayTarget,
    // save primitives
    saveAboutYou,
    buildFuelingProfilePatch,
    save
  };
}

/** The full hook surface — step components + callers type against this. */
export type AboutYouFuel = ReturnType<typeof useAboutYouFuel>;
